<script>
import { fade } from "svelte/transition";
import { onMount, tick } from "svelte";
import { SvelteSet } from "svelte/reactivity";
import { VList } from "virtua/svelte";
import { clickoutside } from "@svelte-put/clickoutside";
import { modalState, hideModal } from "@app/store/modalStore.svelte.js";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { insertHistoryAction } from "@app/store/historyStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import { diff } from "deep-object-diff";
import tcLib from "@cclib/lib/timecode.js";
import normalizeNbsp from "@cclib/functions/quill/normalizeNbsp.js";
import Papa from "papaparse";
import { saveAs } from "file-saver";
import * as m from "@app/paraglide/messages.js";

const STORAGE_KEY = "cc-compare-event-groups-options";
const ROW_HEIGHT = 280;
const ALIGN_BATCH_ROWS = 25;

const COMPARE_OPTIONS = [
    { id: "start", keys: ["start"] },
    { id: "end", keys: ["end"] },
    { id: "text", keys: ["text"] },
    { id: "original", keys: ["original"] },
    { id: "style", keys: ["style"] },
    { id: "position", keys: ["xPos", "yPos"] },
    { id: "alignment", keys: ["alignment"] },
    { id: "writingMode", keys: ["writingMode"] },
    { id: "rate", keys: ["rate"] },
    { id: "tags", keys: ["tags"] },
    { id: "speakers", keys: ["speakers"] },
    { id: "recipients", keys: ["recipients"] },
    { id: "voice", keys: ["voice", "speakingStyle", "voiceStability", "voiceSimilarity"] },
    { id: "notes", keys: ["notes", "notesCheck"] },
    { id: "reply", keys: ["reply", "replyCheck"] },
    { id: "approved", keys: ["approved"] },
    { id: "forced", keys: ["forced"] },
];

function getDefaultEnabledOptions() {
    return Object.fromEntries(COMPARE_OPTIONS.map((option) => [option.id, true]));
}

function loadEnabledOptions() {
    try {
        const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
        if (!stored || typeof stored !== "object") {
            return getDefaultEnabledOptions();
        }
        return {
            ...getDefaultEnabledOptions(),
            ...stored,
        };
    } catch {
        return getDefaultEnabledOptions();
    }
}

function persistEnabledOptions(enabled) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...enabled }));
    } catch {
        // Ignore storage failures (private mode, quota, etc.)
    }
}

function cloneValue(value) {
    if (value === undefined) return undefined;
    return JSON.parse(JSON.stringify(value));
}

function getOptionLabel(id) {
    switch (id) {
        case "start":
            return m.compare_event_groups_option_start();
        case "end":
            return m.compare_event_groups_option_end();
        case "text":
            return m.compare_event_groups_option_text();
        case "original":
            return m.compare_event_groups_option_original();
        case "style":
            return m.compare_event_groups_option_style();
        case "position":
            return m.compare_event_groups_option_position();
        case "alignment":
            return m.compare_event_groups_option_alignment();
        case "writingMode":
            return m.compare_event_groups_option_writing_mode();
        case "rate":
            return m.compare_event_groups_option_rate();
        case "tags":
            return m.compare_event_groups_option_tags();
        case "speakers":
            return m.compare_event_groups_option_speakers();
        case "recipients":
            return m.compare_event_groups_option_recipients();
        case "voice":
            return m.compare_event_groups_option_voice();
        case "notes":
            return m.compare_event_groups_option_notes();
        case "reply":
            return m.compare_event_groups_option_reply();
        case "approved":
            return m.compare_event_groups_option_approved();
        case "forced":
            return m.compare_event_groups_option_forced();
        default:
            return id;
    }
}

function isEmptyPropertyValue(value) {
    return value === undefined || value === null || value === "";
}

function formatEventPropertyDisplay(event, optionId) {
    switch (optionId) {
        case "start":
        case "end": {
            const value = event[optionId];
            const tc = typeof value === "number"
                ? tcLib.secToTc(value, projectState.frameRate, projectState.dropFrame)
                : "";
            return { type: "timecode", text: tc || m.compare_event_groups_not_set() };
        }
        case "text":
        case "original":
            return { type: "html", html: event[optionId] || "" };
        case "position": {
            const xPos = event.xPos;
            const yPos = event.yPos;
            if (isEmptyPropertyValue(xPos) && isEmptyPropertyValue(yPos)) {
                return { type: "simple", text: m.compare_event_groups_not_set() };
            }
            return {
                type: "simple",
                text: `${xPos ?? m.compare_event_groups_not_set()}, ${yPos ?? m.compare_event_groups_not_set()}`,
            };
        }
        case "voice": {
            const name = event.voice?.name || m.compare_event_groups_none_label();
            const style = event.speakingStyle || m.compare_event_groups_none_label();
            const stability = event.voiceStability;
            const similarity = event.voiceSimilarity;
            const stabilityText = stability === undefined || stability === null
                ? m.compare_event_groups_not_set()
                : `${stability}%`;
            const similarityText = similarity === undefined || similarity === null
                ? m.compare_event_groups_not_set()
                : `${similarity}%`;
            return {
                type: "simple",
                text: `${name} · ${style} · ${stabilityText} / ${similarityText}`,
            };
        }
        case "tags":
        case "speakers":
        case "recipients":
            return {
                type: "badges",
                items: Array.isArray(event[optionId]) ? event[optionId] : [],
            };
        case "notes":
            return {
                type: "notes",
                text: event.notes || "",
                checked: !!event.notesCheck,
            };
        case "reply":
            return {
                type: "notes",
                text: event.reply || "",
                checked: !!event.replyCheck,
            };
        case "approved":
        case "forced": {
            const value = event[optionId];
            if (value === undefined || value === null) {
                return { type: "simple", text: m.compare_event_groups_not_set() };
            }
            return { type: "simple", text: String(value) };
        }
        default: {
            const value = event[optionId];
            if (isEmptyPropertyValue(value)) {
                return { type: "simple", text: m.compare_event_groups_not_set() };
            }
            return { type: "simple", text: String(value) };
        }
    }
}

// State for comparison
let selectedGroupA = $state(0);
let selectedGroupB = $state(eventGroupState.length > 1 ? 1 : 0);
let showOnlyDifferences = $state(false);
let selectedRowIndex = $state(null);
let enabledOptions = $state(loadEnabledOptions());
let optionsMenuOpen = $state(false);
let alignedEvents = $state([]);
let aligning = $state(true);
let alignRequestId = 0;

// VList state
let listRef = $state(null);
let listHeight = $state(500);
let resizeObserver;
let compareContentElement;

// Height calculation for VList
function calculateListHeight() {
    if (!compareContentElement) return;
    const modalBody = document.querySelector('#CompareEventGroupsModal .modal-body');
    const headerHeight = document.querySelector('#CompareEventGroupsModal .compare-header')?.offsetHeight || 0;
    const columnHeadersHeight = document.querySelector('#CompareEventGroupsModal .column-headers')?.offsetHeight || 0;
    const footerHeight = document.querySelector('#CompareEventGroupsModal .compare-footer')?.offsetHeight || 0;
    
    if (modalBody) {
        listHeight = modalBody.clientHeight - headerHeight - columnHeadersHeight - footerHeight - 10;
    }
}

onMount(() => {
    compareContentElement = document.querySelector('#CompareEventGroupsModal .compare-content');
    
    // Small delay to ensure modal is fully rendered
    setTimeout(() => {
        calculateListHeight();
    }, 100);

    // Create a resize observer to recalculate height when container changes
    resizeObserver = new ResizeObserver(() => {
        calculateListHeight();
    });

    const modalBody = document.querySelector('#CompareEventGroupsModal .modal-body');
    if (modalBody) {
        resizeObserver.observe(modalBody);
    }
    const compareHeader = document.querySelector('#CompareEventGroupsModal .compare-header');
    if (compareHeader) {
        resizeObserver.observe(compareHeader);
    }

    window.addEventListener('resize', calculateListHeight);
    scheduleAlignment();

    return () => {
        alignRequestId += 1;
        window.removeEventListener('resize', calculateListHeight);
        if (resizeObserver) {
            resizeObserver.disconnect();
        }
    };
});

// Computed values
let groupA = $derived(eventGroupState[selectedGroupA]);
let groupB = $derived(eventGroupState[selectedGroupB]);

// Helper function to calculate similarity between two events
function calculateEventSimilarity(eventA, eventB) {
    if (!eventA || !eventB) return 0;
    
    let score = 0;
    let maxScore = 0;
    
    // Text similarity (most important - 50% weight)
    const textA = (eventA.text || '').replace(/<[^>]*>/g, '').toLowerCase().trim();
    const textB = (eventB.text || '').replace(/<[^>]*>/g, '').toLowerCase().trim();
    
    if (textA && textB) {
        // Simple text similarity using common words
        const wordsA = textA.split(/\s+/);
        const wordsB = textB.split(/\s+/);
        const commonWords = wordsA.filter(word => wordsB.includes(word)).length;
        const totalWords = Math.max(wordsA.length, wordsB.length);
        
        if (totalWords > 0) {
            score += (commonWords / totalWords) * 50;
        }
    }
    maxScore += 50;
    
    // Time overlap similarity (30% weight)
    if (typeof eventA.start === 'number' && typeof eventA.end === 'number' && 
        typeof eventB.start === 'number' && typeof eventB.end === 'number') {
        
        const overlapStart = Math.max(eventA.start, eventB.start);
        const overlapEnd = Math.min(eventA.end, eventB.end);
        const overlap = Math.max(0, overlapEnd - overlapStart);
        
        const durationA = eventA.end - eventA.start;
        const durationB = eventB.end - eventB.start;
        const maxDuration = Math.max(durationA, durationB);
        
        if (maxDuration > 0) {
            score += (overlap / maxDuration) * 30;
        }
    }
    maxScore += 30;
    
    // Start time proximity (20% weight)
    if (typeof eventA.start === 'number' && typeof eventB.start === 'number') {
        const timeDiff = Math.abs(eventA.start - eventB.start);
        const proximityScore = Math.max(0, 1 - (timeDiff / 10)); // 10 second window
        score += proximityScore * 20;
    }
    maxScore += 20;
    
    return maxScore > 0 ? score / maxScore : 0;
}

function yieldToBrowser() {
    return new Promise((resolve) => setTimeout(resolve, 0));
}

async function alignEventsAsync(eventsA, eventsB, isCancelled) {
    if (!eventsA?.length && !eventsB?.length) return [];
    if (!eventsA?.length) {
        return eventsB.map((_, i) => ({ type: 'added', indexA: null, indexB: i }));
    }
    if (!eventsB?.length) {
        return eventsA.map((_, i) => ({ type: 'deleted', indexA: i, indexB: null }));
    }
    
    const lenA = eventsA.length;
    const lenB = eventsB.length;
    
    const dp = Array(lenA + 1).fill().map(() => Array(lenB + 1).fill(0));
    const trace = Array(lenA + 1).fill().map(() => Array(lenB + 1).fill(''));
    
    for (let i = 1; i <= lenA; i++) {
        if (isCancelled()) return null;
        for (let j = 1; j <= lenB; j++) {
            const similarity = calculateEventSimilarity(eventsA[i-1], eventsB[j-1]);
            const matchScore = dp[i-1][j-1] + similarity;
            const deleteScore = dp[i-1][j] - 0.1;
            const insertScore = dp[i][j-1] - 0.1;
            
            if (matchScore >= deleteScore && matchScore >= insertScore) {
                dp[i][j] = matchScore;
                trace[i][j] = 'match';
            } else if (deleteScore >= insertScore) {
                dp[i][j] = deleteScore;
                trace[i][j] = 'delete';
            } else {
                dp[i][j] = insertScore;
                trace[i][j] = 'insert';
            }
        }
        if (i % ALIGN_BATCH_ROWS === 0) {
            await yieldToBrowser();
        }
    }

    if (isCancelled()) return null;
    
    const alignment = [];
    let i = lenA, j = lenB;
    
    while (i > 0 || j > 0) {
        if (i > 0 && j > 0 && trace[i][j] === 'match') {
            const similarity = calculateEventSimilarity(eventsA[i-1], eventsB[j-1]);
            alignment.unshift({
                type: similarity > 0.3 ? 'matched' : 'modified',
                indexA: i-1,
                indexB: j-1,
                similarity
            });
            i--; j--;
        } else if (i > 0 && (j === 0 || trace[i][j] === 'delete')) {
            alignment.unshift({
                type: 'deleted',
                indexA: i-1,
                indexB: null
            });
            i--;
        } else {
            alignment.unshift({
                type: 'added',
                indexA: null,
                indexB: j-1
            });
            j--;
        }
    }
    
    return alignment;
}

async function scheduleAlignment() {
    const requestId = ++alignRequestId;
    aligning = true;
    alignedEvents = [];
    selectedRowIndex = null;
    await tick();
    if (requestId !== alignRequestId) return;

    const eventsA = eventGroupState[selectedGroupA]?.events;
    const eventsB = eventGroupState[selectedGroupB]?.events;
    const result = await alignEventsAsync(
        eventsA,
        eventsB,
        () => requestId !== alignRequestId,
    );
    if (requestId !== alignRequestId) return;

    alignedEvents = result || [];
    aligning = false;
}

// Helper function to format time differences as SMPTE timecode
function formatTimeDifference(oldValue, newValue, changeKey) {
    if ((changeKey === 'start' || changeKey === 'end') && typeof oldValue === 'number' && typeof newValue === 'number') {
        const oldTc = tcLib.secToTc(oldValue, projectState.frameRate, projectState.dropFrame);
        const newTc = tcLib.secToTc(newValue, projectState.frameRate, projectState.dropFrame);
        const delta = newValue - oldValue;
        const diffTc = tcLib.secToTc(Math.abs(delta), projectState.frameRate, projectState.dropFrame);
        const direction = delta > 0 ? '+' : '-';
        
        return {
            type: 'timecode',
            oldTc,
            newTc,
            diffTc,
            direction,
            seconds: delta
        };
    }
    return null;
}

// Helper function to format change display text
function formatChangeDisplay(changeKey, changeValue, eventA, eventB) {
    const oldValue = eventA[changeKey];
    const newValue = eventB[changeKey];
    
    // Special handling for timecode changes
    const timeDiff = formatTimeDifference(oldValue, newValue, changeKey);
    if (timeDiff) {
        return {
            type: 'timecode',
            display: `${timeDiff.oldTc} → ${timeDiff.newTc} (${timeDiff.direction}${timeDiff.diffTc})`,
            class: 'timecode-change'
        };
    }
    
    // Special handling for badge-style arrays (speakers, recipients, tags)
    if (['speakers', 'recipients', 'tags'].includes(changeKey)) {
        const oldArray = Array.isArray(oldValue) ? oldValue : [];
        const newArray = Array.isArray(newValue) ? newValue : [];
        
        return {
            type: 'badges',
            oldArray,
            newArray,
            class: 'badge-change'
        };
    }
    
    // Special handling for voice property (object with name)
    if (changeKey === 'voice') {
        const oldVoice = oldValue?.name || m.compare_event_groups_none_label();
        const newVoice = newValue?.name || m.compare_event_groups_none_label();
        
        return {
            type: 'voice',
            display: `${oldVoice} → ${newVoice}`,
            class: 'voice-change'
        };
    }
    
    // Special handling for text, notes, and reply (truncate if too long)
    if (changeKey === 'text' || changeKey === 'original' || changeKey === 'notes' || changeKey === 'reply') {
        const stripHtml = changeKey === 'text' || changeKey === 'original';
        const oldText = stripHtml
            ? normalizeNbsp(oldValue || '').replace(/<[^>]*>/g, '').substring(0, 30)
            : String(oldValue || '').substring(0, 30);
        const newText = stripHtml
            ? normalizeNbsp(newValue || '').replace(/<[^>]*>/g, '').substring(0, 30)
            : String(newValue || '').substring(0, 30);
        return {
            type: 'text',
            display: `"${oldText}${oldText.length >= 30 ? '...' : ''}" → "${newText}${newText.length >= 30 ? '...' : ''}"`,
            class: 'text-change'
        };
    }
    
    // Default handling for other changes
    if (typeof changeValue === 'object') {
        return {
            type: 'object',
            display: JSON.stringify(changeValue),
            class: 'object-change'
        };
    }
    
    return {
        type: 'simple',
        display: `${oldValue} → ${newValue}`,
        class: 'simple-change'
    };
}

function getEnabledCompareKeys() {
    const keys = [];
    for (const option of COMPARE_OPTIONS) {
        if (enabledOptions[option.id]) {
            keys.push(...option.keys);
        }
    }
    return keys;
}

function extractComparableProperties(event) {
    if (!event) return null;
    
    const comparable = {};
    for (const prop of getEnabledCompareKeys()) {
        if (Object.hasOwn(event, prop)) {
            // Special handling for boolean properties that might be undefined
            if ((prop === 'approved' || prop === 'forced') && event[prop] === undefined) {
                continue;
            }
            comparable[prop] = (prop === 'text' || prop === 'original')
                ? normalizeNbsp(event[prop])
                : event[prop];
        }
    }
    return comparable;
}

function groupChangedProperties(changes) {
    if (!changes) return [];

    const groups = [];
    const assigned = new SvelteSet();

    for (const changeKey of Object.keys(changes)) {
        if (assigned.has(changeKey)) continue;

        const option = COMPARE_OPTIONS.find((opt) => opt.keys.includes(changeKey));
        if (option) {
            const changeKeys = option.keys.filter((key) => Object.hasOwn(changes, key));
            for (const key of option.keys) {
                assigned.add(key);
            }
            groups.push({
                optionId: option.id,
                keys: option.keys,
                changeKeys,
                label: getOptionLabel(option.id),
            });
        } else {
            assigned.add(changeKey);
            groups.push({
                optionId: changeKey,
                keys: [changeKey],
                changeKeys: [changeKey],
                label: changeKey,
            });
        }
    }

    return groups;
}

function toggleOptionsMenu() {
    optionsMenuOpen = !optionsMenuOpen;
}

function closeOptionsMenu() {
    optionsMenuOpen = false;
}

function saveCurrentOptions() {
    persistEnabledOptions(enabledOptions);
}

function selectAllOptions() {
    for (const option of COMPARE_OPTIONS) {
        enabledOptions[option.id] = true;
    }
    saveCurrentOptions();
}

function selectNoneOptions() {
    for (const option of COMPARE_OPTIONS) {
        enabledOptions[option.id] = false;
    }
    saveCurrentOptions();
}

function calculateDifferenceForAligned(aligned, index, eventA, eventB) {
    if (!aligned) return null;
    
    if (aligned.type === 'matched') {
        const comparableA = extractComparableProperties(eventA);
        const comparableB = extractComparableProperties(eventB);
        const diffResult = diff(comparableA, comparableB);
        
        if (Object.keys(diffResult).length > 0) {
            return {
                index,
                type: 'modified',
                changes: diffResult,
                eventA,
                eventB,
                similarity: aligned.similarity
            };
        }
        return null;
    } else if (aligned.type === 'modified') {
        const comparableA = extractComparableProperties(eventA);
        const comparableB = extractComparableProperties(eventB);
        const diffResult = diff(comparableA, comparableB);
        
        if (Object.keys(diffResult).length > 0) {
            return {
                index,
                type: 'modified',
                changes: diffResult,
                eventA,
                eventB,
                similarity: aligned.similarity
            };
        }
        return null;
    } else if (aligned.type === 'deleted') {
        return {
            index,
            type: 'deleted',
            eventA,
            eventB: null
        };
    } else if (aligned.type === 'added') {
        return {
            index,
            type: 'added',
            eventA: null,
            eventB
        };
    }
    return null;
}

function getLiveEventA(aligned) {
    return aligned?.indexA !== null && aligned?.indexA !== undefined
        ? groupA?.events?.[aligned.indexA]
        : null;
}

function getLiveEventB(aligned) {
    return aligned?.indexB !== null && aligned?.indexB !== undefined
        ? groupB?.events?.[aligned.indexB]
        : null;
}

// Filter aligned events based on showOnlyDifferences toggle
let filteredAlignedEvents = $derived.by(() => {
    getEnabledCompareKeys();
    if (!showOnlyDifferences) return [...alignedEvents];
    
    return alignedEvents.filter(aligned => {
        if (aligned.type === 'added' || aligned.type === 'deleted') {
            return true;
        }
        
        if (aligned.type === 'matched' || aligned.type === 'modified') {
            const comparableA = extractComparableProperties(getLiveEventA(aligned));
            const comparableB = extractComparableProperties(getLiveEventB(aligned));
            const diffResult = diff(comparableA, comparableB);
            
            return Object.keys(diffResult).length > 0;
        }
        
        return false;
    });
});

// Find next/previous differences based on filtered aligned events
let allDifferences = $derived.by(() => {
    if (!filteredAlignedEvents.length) return [];
    
    // If showing only differences, every filtered event is a difference
    if (showOnlyDifferences) {
        return Array.from({ length: filteredAlignedEvents.length }, (_, i) => i);
    }
    
    // Otherwise, find which events have actual differences
    const differences = [];
    
    for (let i = 0; i < filteredAlignedEvents.length; i++) {
        const aligned = filteredAlignedEvents[i];
        
        if (aligned.type === 'added' || aligned.type === 'deleted') {
            differences.push(i);
        } else if (aligned.type === 'matched' || aligned.type === 'modified') {
            const comparableA = extractComparableProperties(getLiveEventA(aligned));
            const comparableB = extractComparableProperties(getLiveEventB(aligned));
            const diffResult = diff(comparableA, comparableB);
            
            if (Object.keys(diffResult).length > 0) {
                differences.push(i);
            }
        }
    }
    return differences;
});

let nextDifferenceIndex = $derived.by(() => {
    if (!allDifferences.length) return undefined;
    const currentIndex = selectedRowIndex !== null ? selectedRowIndex : -1;
    return allDifferences.find(index => index > currentIndex);
});

let previousDifferenceIndex = $derived.by(() => {
    if (!allDifferences.length) return undefined;
    const currentIndex = selectedRowIndex !== null ? selectedRowIndex : allDifferences[allDifferences.length - 1] + 1;
    return [...allDifferences].reverse().find(index => index < currentIndex);
});

// Navigation functions
function goToNextDifference() {
    if (nextDifferenceIndex !== undefined && listRef) {
        selectedRowIndex = nextDifferenceIndex;
        listRef.scrollToIndex(nextDifferenceIndex, {
            align: "center",
            smooth: true
        });
    }
}

function goToPreviousDifference() {
    if (previousDifferenceIndex !== undefined && listRef) {
        selectedRowIndex = previousDifferenceIndex;
        listRef.scrollToIndex(previousDifferenceIndex, {
            align: "center",
            smooth: true
        });
    }
}

// Row selection handler
function selectRow(index) {
    selectedRowIndex = selectedRowIndex === index ? null : index;
    
    // Scroll to selected row using VList
    if (selectedRowIndex !== null && listRef) {
        listRef.scrollToIndex(selectedRowIndex, {
            align: "center",
            smooth: true
        });
    }
}

function getDifferenceLabel(type) {
    switch (type) {
        case 'modified':
            return m.compare_event_groups_diff_type_modified();
        case 'added':
            return m.compare_event_groups_diff_type_added();
        case 'deleted':
            return m.compare_event_groups_diff_type_deleted();
        default:
            return type;
    }
}

// Helper function to generate a new unique ID for an event
function generateNewEventId() {
    return crypto.randomUUID();
}

function recordHistory(name, eventGroupIndex) {
    insertHistoryAction({
        name,
        eventGroup: eventGroupIndex,
        snapshots: [{
            store: "eventGroupState",
            value: JSON.stringify(eventGroupState),
        }],
    });
}

function getChangedEnabledKeys(sourceEvent, targetEvent) {
    const comparableSource = extractComparableProperties(sourceEvent);
    const comparableTarget = extractComparableProperties(targetEvent);
    return Object.keys(diff(comparableSource, comparableTarget));
}

function mergeEventProperties(sourceEvent, targetEvent, keys) {
    const merged = JSON.parse(JSON.stringify(targetEvent));
    merged.id = targetEvent.id;
    for (const key of keys) {
        merged[key] = cloneValue(sourceEvent[key]);
    }
    return merged;
}

function insertEventByStartTime(events, newEvent) {
    let insertPosition = events.length;
    if (typeof newEvent.start === "number") {
        for (let i = 0; i < events.length; i++) {
            if (events[i].start > newEvent.start) {
                insertPosition = i;
                break;
            }
        }
    }
    events.splice(insertPosition, 0, newEvent);
}

function applyPropertyChange(direction, indexA, indexB, keys) {
    const isAtoB = direction === "AtoB";
    const sourceGroupIndex = isAtoB ? selectedGroupA : selectedGroupB;
    const targetGroupIndex = isAtoB ? selectedGroupB : selectedGroupA;
    const sourceIndex = isAtoB ? indexA : indexB;
    const targetIndex = isAtoB ? indexB : indexA;

    const sourceEvent = eventGroupState[sourceGroupIndex]?.events?.[sourceIndex];
    const targetEvent = eventGroupState[targetGroupIndex]?.events?.[targetIndex];
    if (!sourceEvent || !targetEvent || !keys?.length || aligning) return;

    eventGroupState[targetGroupIndex].events[targetIndex] = mergeEventProperties(
        sourceEvent,
        targetEvent,
        keys,
    );

    recordHistory(
        isAtoB ? "apply property A to B" : "apply property B to A",
        targetGroupIndex,
    );
    toast.push(
        isAtoB
            ? m.compare_event_groups_property_applied_a_to_b()
            : m.compare_event_groups_property_applied_b_to_a(),
        { classes: ["toast-success"] },
    );
}

function applyChangeAtoB(indexA, indexB) {
    const sourceEvent = indexA !== null ? eventGroupState[selectedGroupA]?.events?.[indexA] : null;
    if (!sourceEvent) return;

    const targetExists =
        indexB !== null && indexB < eventGroupState[selectedGroupB].events.length;

    if (targetExists) {
        const liveTarget = eventGroupState[selectedGroupB].events[indexB];
        const keys = getChangedEnabledKeys(sourceEvent, liveTarget);
        if (!keys.length) return;
        eventGroupState[selectedGroupB].events[indexB] = mergeEventProperties(
            sourceEvent,
            liveTarget,
            keys,
        );
        recordHistory("apply change A to B", selectedGroupB);
        toast.push(m.compare_event_groups_change_applied_a_to_b(), { classes: ["toast-success"] });
        return;
    }

    const newEvent = JSON.parse(JSON.stringify(sourceEvent));
    newEvent.id = generateNewEventId();
    insertEventByStartTime(eventGroupState[selectedGroupB].events, newEvent);
    recordHistory("apply change A to B", selectedGroupB);
    toast.push(m.compare_event_groups_change_applied_a_to_b(), { classes: ["toast-success"] });
    scheduleAlignment();
}

function applyChangeBtoA(indexA, indexB) {
    const sourceEvent = indexB !== null ? eventGroupState[selectedGroupB]?.events?.[indexB] : null;
    if (!sourceEvent) return;

    const targetExists =
        indexA !== null && indexA < eventGroupState[selectedGroupA].events.length;

    if (targetExists) {
        const liveTarget = eventGroupState[selectedGroupA].events[indexA];
        const keys = getChangedEnabledKeys(sourceEvent, liveTarget);
        if (!keys.length) return;
        eventGroupState[selectedGroupA].events[indexA] = mergeEventProperties(
            sourceEvent,
            liveTarget,
            keys,
        );
        recordHistory("apply change B to A", selectedGroupA);
        toast.push(m.compare_event_groups_change_applied_b_to_a(), { classes: ["toast-success"] });
        return;
    }

    const newEvent = JSON.parse(JSON.stringify(sourceEvent));
    newEvent.id = generateNewEventId();
    insertEventByStartTime(eventGroupState[selectedGroupA].events, newEvent);
    recordHistory("apply change B to A", selectedGroupA);
    toast.push(m.compare_event_groups_change_applied_b_to_a(), { classes: ["toast-success"] });
    scheduleAlignment();
}

function applyAllChanges(direction) {
    const isAtoB = direction === "AtoB";
    const sourceGroupIndex = isAtoB ? selectedGroupA : selectedGroupB;
    const targetGroupIndex = isAtoB ? selectedGroupB : selectedGroupA;
    const targetEvents = eventGroupState[targetGroupIndex].events;
    const result = [];

    for (const aligned of alignedEvents) {
        const sourceIndex = isAtoB ? aligned.indexA : aligned.indexB;
        const targetIndex = isAtoB ? aligned.indexB : aligned.indexA;
        const liveSource =
            sourceIndex !== null
                ? eventGroupState[sourceGroupIndex].events[sourceIndex]
                : null;
        const liveTarget =
            targetIndex !== null ? targetEvents[targetIndex] : null;

        if (liveSource && liveTarget) {
            const keys = getChangedEnabledKeys(liveSource, liveTarget);
            if (keys.length) {
                result.push(mergeEventProperties(liveSource, liveTarget, keys));
            } else {
                result.push(liveTarget);
            }
        } else if (liveSource && !liveTarget) {
            const newEvent = JSON.parse(JSON.stringify(liveSource));
            newEvent.id = generateNewEventId();
            result.push(newEvent);
        } else if (!liveSource && liveTarget) {
            result.push(liveTarget);
        }
    }

    eventGroupState[targetGroupIndex].events = result;
    recordHistory(
        isAtoB ? "apply all changes A to B" : "apply all changes B to A",
        targetGroupIndex,
    );
    toast.push(
        isAtoB
            ? m.compare_event_groups_all_applied_a_to_b()
            : m.compare_event_groups_all_applied_b_to_a(),
        { classes: ["toast-success"] },
    );
    scheduleAlignment();
}

// CSV Export function
function exportDifferencesToCSV() {
    if (!groupA || !groupB) return;
    
    const csvData = [];
    const maxLength = Math.max(groupA.events.length, groupB.events.length);
    
    for (let i = 0; i < maxLength; i++) {
        const eventA = groupA.events[i];
        const eventB = groupB.events[i];
        
        let changeType = 'no-change';
        let changeTypeLabel = '';
        let changes = '';
        
        if (eventA && eventB) {
            // Extract only comparable properties
            const comparableA = extractComparableProperties(eventA);
            const comparableB = extractComparableProperties(eventB);
            const diffResult = diff(comparableA, comparableB);
            
            if (Object.keys(diffResult).length > 0) {
                changeType = 'modified';
                changeTypeLabel = m.compare_event_groups_csv_change_type_modified();
                changes = JSON.stringify(diffResult, null, 2);
            }
        } else if (eventA && !eventB) {
            changeType = 'deleted';
            changeTypeLabel = m.compare_event_groups_csv_change_type_deleted();
            changes = m.compare_event_groups_csv_deleted_description();
        } else if (!eventA && eventB) {
            changeType = 'added';
            changeTypeLabel = m.compare_event_groups_csv_change_type_added();
            changes = m.compare_event_groups_csv_added_description();
        }
        
        if (changeType !== 'no-change') {
            csvData.push({
                [m.compare_event_groups_csv_event_index_header()]: i + 1,
                [m.compare_event_groups_csv_change_type_header()]: changeTypeLabel,
                [m.compare_event_groups_csv_group_a_text_header()]: eventA?.text || '',
                [m.compare_event_groups_csv_group_a_start_header()]: eventA?.start || '',
                [m.compare_event_groups_csv_group_a_end_header()]: eventA?.end || '',
                [m.compare_event_groups_csv_group_a_notes_header()]: eventA?.notes || '',
                [m.compare_event_groups_csv_group_a_reply_header()]: eventA?.reply || '',
                [m.compare_event_groups_csv_group_b_text_header()]: eventB?.text || '',
                [m.compare_event_groups_csv_group_b_start_header()]: eventB?.start || '',
                [m.compare_event_groups_csv_group_b_end_header()]: eventB?.end || '',
                [m.compare_event_groups_csv_group_b_notes_header()]: eventB?.notes || '',
                [m.compare_event_groups_csv_group_b_reply_header()]: eventB?.reply || '',
                [m.compare_event_groups_csv_changes_header()]: changes
            });
        }
    }
    
    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const filename = `event-group-comparison-${groupA.name}-vs-${groupB.name}-${new Date().toISOString().split('T')[0]}.csv`;
    saveAs(blob, filename);
    
    toast.push(m.compare_event_groups_exported_csv_toast(), { classes: ["toast-success"] });
}
</script>

{#snippet eventCardProperties(event)}
    {#each COMPARE_OPTIONS as option (option.id)}
        {#if enabledOptions[option.id] || (option.id === "notes" && event.notes) || (option.id === "reply" && event.reply)}
            {@const display = formatEventPropertyDisplay(event, option.id)}
            {#if option.id === "start" || option.id === "end"}
                <div class="event-prop event-prop-timecode small text-muted">
                    {option.id === "start" ? m.compare_event_groups_start_label() : m.compare_event_groups_end_label()}
                    <span class="text-dark">{display.text}</span>
                </div>
            {:else if option.id === "text" || option.id === "original"}
                <div class="event-prop event-prop-html">
                    {#if option.id === "original"}
                        <div class="event-prop-label small text-muted">{getOptionLabel(option.id)}</div>
                    {/if}
                    <div class="text-content EditorPreviewText text-center">
                        {@html display.html || `<em class="text-muted">${m.compare_event_groups_no_text()}</em>`}
                    </div>
                </div>
            {:else if display.type === "badges"}
                <div class="event-prop event-prop-badges">
                    <div class="event-prop-label small text-muted">{getOptionLabel(option.id)}</div>
                    <div class="event-prop-badge-list">
                        {#each display.items as item (item.id ?? item.name ?? item)}
                            <span
                                class="badge badge-sm me-1 {item.colour === 'yellow' ? 'text-dark' : 'text-white'}"
                                style="background-color: {item.colour || '#6c757d'};">
                                {item.name || item}
                            </span>
                        {:else}
                            <span class="text-muted small">{m.compare_event_groups_none_label()}</span>
                        {/each}
                    </div>
                </div>
            {:else if option.id === "notes" || option.id === "reply"}
                <div class="event-prop event-prop-notes">
                    {#if display.text}
                        <div class="note-line">
                            <div class="note-heading">
                                <i class="bi {option.id === 'notes' ? 'bi-chat-right-text' : 'bi-chat-left-text'} {display.checked ? 'text-primary' : 'text-warning'}"></i>
                                <span class="note-label">{getOptionLabel(option.id)}</span>
                                {#if display.checked}
                                    <span class="badge bg-success">{m.compare_event_groups_done_badge()}</span>
                                {/if}
                            </div>
                            <div class="note-text">{display.text}</div>
                        </div>
                    {:else}
                        <div class="event-prop-simple small">
                            <span class="text-muted">{getOptionLabel(option.id)}</span>
                            <span class="text-dark">{m.compare_event_groups_not_set()}</span>
                        </div>
                    {/if}
                </div>
            {:else}
                <div class="event-prop event-prop-simple small">
                    <span class="text-muted">{getOptionLabel(option.id)}</span>
                    <span class="text-dark">{display.text}</span>
                </div>
            {/if}
        {/if}
    {/each}
{/snippet}

<div 
    transition:fade={{ duration: 100 }} 
    class="modal {modalState.selected === 'compareEventGroups' ? 'show d-block' : ''}" 
    role="dialog" 
    aria-labelledby="compareTitle"
    aria-modal="true"
    tabindex="-1" 
    id="CompareEventGroupsModal">
    <div class="modal-dialog modal-fullscreen">
        <div class="modal-content">
            <div class="modal-header">
                <h5 class="modal-title" id="compareTitle">
                    <i class="bi bi-arrow-left-right me-2"></i>
                    {m.compare_event_groups_title()}
                </h5>
                <button type="button" class="btn-close" onclick={hideModal} aria-label={m.compare_event_groups_close_label()}></button>
            </div>
            
            <div class="modal-body p-0 d-flex flex-column">
                <!-- Header controls -->
                <div class="compare-header p-3 border-bottom flex-shrink-0">
                    <div class="row align-items-center">
                        <div class="col-3">
                            <label class="form-label" for="groupASelect">{m.compare_event_groups_event_group_a_label()}</label>
                            <select
                                class="form-select"
                                id="groupASelect"
                                value={selectedGroupA}
                                onchange={(e) => {
                                    selectedGroupA = Number(e.currentTarget.value);
                                    scheduleAlignment();
                                }}>
                                {#each eventGroupState as group, index (group.id ?? index)}
                                    <option value={index}>{group.name} ({group.type})</option>
                                {/each}
                            </select>
                        </div>
                        
                        <div class="col-6 text-center">
                            <div class="btn-group" role="group">
                                <button 
                                    type="button" 
                                    class="btn btn-outline-primary"
                                    onclick={() => goToPreviousDifference()}
                                    disabled={aligning || previousDifferenceIndex === undefined}>
                                    <i class="bi bi-arrow-left me-1"></i>
                                    {m.compare_event_groups_previous_difference()}
                                </button>
                                <button 
                                    type="button" 
                                    class="btn btn-outline-primary"
                                    onclick={() => goToNextDifference()}
                                    disabled={aligning || nextDifferenceIndex === undefined}>
                                    {m.compare_event_groups_next_difference()}
                                    <i class="bi bi-arrow-right ms-1"></i>
                                </button>
                            </div>
                            
                            <div class="mt-2 d-flex flex-wrap justify-content-center align-items-center gap-2">
                                <button 
                                    type="button" 
                                    class="btn btn-success btn-sm"
                                    onclick={exportDifferencesToCSV}
                                    disabled={aligning}>
                                    <i class="bi bi-download me-1"></i>
                                    {m.compare_event_groups_export_csv()}
                                </button>

                                <span
                                    class="compare-options"
                                    use:clickoutside
                                    onclickoutside={closeOptionsMenu}>
                                    <button
                                        type="button"
                                        class="btn btn-sm {optionsMenuOpen ? 'btn-primary' : 'btn-outline-secondary'}"
                                        onclick={toggleOptionsMenu}
                                        aria-expanded={optionsMenuOpen}
                                        aria-haspopup="true"
                                        title={m.compare_event_groups_options_title()}>
                                        <i class="bi bi-sliders me-1"></i>
                                        {m.compare_event_groups_options()}
                                    </button>
                                    {#if optionsMenuOpen}
                                        <div
                                            class="compare-options-popover shadow border rounded bg-body p-2 text-start"
                                            role="dialog"
                                            aria-label={m.compare_event_groups_options_title()}>
                                            <div class="fw-semibold small mb-2 px-1">
                                                {m.compare_event_groups_options_title()}
                                            </div>
                                            <div class="d-flex gap-1 mb-2">
                                                <button
                                                    type="button"
                                                    class="btn btn-outline-secondary btn-sm py-0"
                                                    onclick={selectAllOptions}>
                                                    {m.compare_event_groups_select_all()}
                                                </button>
                                                <button
                                                    type="button"
                                                    class="btn btn-outline-secondary btn-sm py-0"
                                                    onclick={selectNoneOptions}>
                                                    {m.compare_event_groups_select_none()}
                                                </button>
                                            </div>
                                            <div class="row g-1">
                                                {#each COMPARE_OPTIONS as option (option.id)}
                                                    <div class="col-6">
                                                        <div class="form-check mb-1">
                                                            <input
                                                                type="checkbox"
                                                                class="form-check-input"
                                                                id="compare-option-{option.id}"
                                                                bind:checked={enabledOptions[option.id]}
                                                                onchange={saveCurrentOptions} />
                                                            <label
                                                                class="form-check-label small"
                                                                for="compare-option-{option.id}">
                                                                {getOptionLabel(option.id)}
                                                            </label>
                                                        </div>
                                                    </div>
                                                {/each}
                                            </div>
                                        </div>
                                    {/if}
                                </span>
                                
                                <div class="form-check form-check-inline mb-0">
                                    <input 
                                        class="form-check-input" 
                                        type="checkbox" 
                                        id="showDifferencesOnly" 
                                        bind:checked={showOnlyDifferences}>
                                    <label class="form-check-label" for="showDifferencesOnly">
                                        {m.compare_event_groups_show_differences_only()}
                                    </label>
                                </div>
                            </div>

                            <div class="btn-group mt-2" role="group">
                                <button
                                    type="button"
                                    class="btn btn-outline-primary btn-sm"
                                    onclick={() => applyAllChanges("AtoB")}
                                    disabled={aligning || selectedGroupA === selectedGroupB}
                                    title={m.compare_event_groups_apply_all_a_to_b_title()}>
                                    {m.compare_event_groups_apply_all_a_to_b()}
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-outline-primary btn-sm"
                                    onclick={() => applyAllChanges("BtoA")}
                                    disabled={aligning || selectedGroupA === selectedGroupB}
                                    title={m.compare_event_groups_apply_all_b_to_a_title()}>
                                    {m.compare_event_groups_apply_all_b_to_a()}
                                </button>
                            </div>
                        </div>
                        
                        <div class="col-3">
                            <label class="form-label" for="groupBSelect">{m.compare_event_groups_event_group_b_label()}</label>
                            <select
                                class="form-select"
                                id="groupBSelect"
                                value={selectedGroupB}
                                onchange={(e) => {
                                    selectedGroupB = Number(e.currentTarget.value);
                                    scheduleAlignment();
                                }}>
                                {#each eventGroupState as group, index (group.id ?? index)}
                                    <option value={index}>{group.name} ({group.type})</option>
                                {/each}
                            </select>
                        </div>
                    </div>
                </div>
                
                <!-- Comparison content -->
                <div class="compare-content flex-grow-1" bind:this={compareContentElement} style:--compare-row-height="{ROW_HEIGHT}px">
                    <!-- Column Headers -->
                    <div class="column-headers">
                        <div class="row g-0">
                            <div class="col-4 border-end">
                                <div class="column-header p-2 bg-primary text-white">
                                    <h6 class="mb-0">
                                        <i class="bi bi-file-text me-1"></i>
                                        {m.compare_event_groups_group_events_label({ name: groupA?.name || m.compare_event_groups_group_a_fallback() })}
                                    </h6>
                                </div>
                            </div>
                            <div class="col-4 border-end">
                                <div class="column-header p-2 bg-warning text-dark">
                                    <h6 class="mb-0">
                                        <i class="bi bi-arrows-expand me-1"></i>
                                        {m.compare_event_groups_differences_actions_heading()}
                                    </h6>
                                </div>
                            </div>
                            <div class="col-4">
                                <div class="column-header p-2 bg-primary text-white">
                                    <h6 class="mb-0">
                                        <i class="bi bi-file-text me-1"></i>
                                        {m.compare_event_groups_group_events_label({ name: groupB?.name || m.compare_event_groups_group_b_fallback() })}
                                    </h6>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {#if aligning}
                        <div class="aligning-status d-flex flex-column align-items-center justify-content-center" style="height:{listHeight}px;" role="status" aria-live="polite">
                            <div class="spinner-border text-primary" aria-hidden="true"></div>
                            <p class="mt-3 mb-0 text-muted">{m.compare_event_groups_aligning()}</p>
                        </div>
                    {:else}
                    <VList 
                        bind:this={listRef}
                        style="height:{listHeight}px; scrollbar-gutter: stable;"
                        data={filteredAlignedEvents}
                        getKey={(item, index) => `${item.indexA}-${item.indexB}-${index}-${COMPARE_OPTIONS.map((opt) => enabledOptions[opt.id] ? 1 : 0).join("")}`}
                        itemSize={ROW_HEIGHT}
                    >
                        {#snippet children(aligned, alignedIndex)}
                            {@const eventA = getLiveEventA(aligned)}
                            {@const eventB = getLiveEventB(aligned)}
                            {@const difference = calculateDifferenceForAligned(aligned, alignedIndex, eventA, eventB)}
                            
                            <div class="synchronized-row row g-0 {selectedRowIndex === alignedIndex ? 'selected-row' : ''}" 
                                 data-event-index={alignedIndex}
                                 onclick={() => selectRow(alignedIndex)}
                                 onkeydown={(e) => e.key === 'Enter' || e.key === ' ' ? selectRow(alignedIndex) : null}
                                 role="button"
                                 tabindex="0">
                                <!-- Group A Column -->
                                <div class="col-4 border-end disabled-interaction">
                                    <div class="event-container">
                                        {#if eventA}
                                            <div class="event-wrapper">
                                                <div class="simple-event-display p-2">
                                                    <div class="event-number fw-bold text-muted mb-1">
                                                        A{aligned.indexA !== null ? aligned.indexA + 1 : '?'}
                                                        {#if aligned.similarity !== undefined}
                                                            <span class="similarity-badge badge badge-secondary ms-1" title={m.compare_event_groups_similarity_title({ percent: Math.round(aligned.similarity * 100) })}>
                                                                {Math.round(aligned.similarity * 100)}%
                                                            </span>
                                                        {/if}
                                                    </div>
                                                    <div class="event-card-properties">
                                                        {@render eventCardProperties(eventA)}
                                                    </div>
                                                </div>
                                            </div>
                                        {:else}
                                            <div class="event-wrapper empty-event">
                                                <div class="p-3 text-muted text-center">
                                                    <i class="bi bi-dash-circle"></i>
                                                    {m.compare_event_groups_no_event_deleted()}
                                                </div>
                                            </div>
                                        {/if}
                                    </div>
                                </div>
                        
                                <!-- Differences Column -->
                                <div class="col-4 border-end">
                                    <div class="difference-container">
                                        <div class="difference-item">
                                            {#if difference}
                                                <div class="difference-header p-2 bg-light">
                                                    <small class="text-muted">{m.compare_event_groups_alignment_label({ index: alignedIndex + 1 })}</small>
                                                    <span class="badge bg-{difference.type === 'modified' ? 'warning' : difference.type === 'added' ? 'success' : 'danger'} ms-2">
                                                        {getDifferenceLabel(difference.type)}
                                                    </span>
                                                </div>
                                                
                                                <div class="difference-content">
                                                    {#if difference.type === 'modified'}
                                                        <div class="p-2">
                                                            <div class="changes-summary mb-2">
                                                                {#each groupChangedProperties(difference.changes) as group (group.optionId)}
                                                                    <div class="change-item change-item-compact">
                                                                        <div class="change-item-header">
                                                                            <div class="change-label">{group.label}</div>
                                                                            <div class="change-property-actions">                                
                                                                                <button
                                                                                    type="button"
                                                                                    class="btn btn-outline-primary py-0 px-1"
                                                                                    onclick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        applyPropertyChange("BtoA", aligned.indexA, aligned.indexB, group.keys);
                                                                                    }}
                                                                                    title={m.compare_event_groups_apply_property_b_to_a_title()}>
                                                                                    ← A
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    class="btn btn-outline-primary py-0 px-1"
                                                                                    onclick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        applyPropertyChange("AtoB", aligned.indexA, aligned.indexB, group.keys);
                                                                                    }}
                                                                                    title={m.compare_event_groups_apply_property_a_to_b_title()}>
                                                                                    B → 
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                        {#each group.changeKeys as changeKey (changeKey)}
                                                                            {@const changeDisplay = formatChangeDisplay(changeKey, difference.changes[changeKey], difference.eventA, difference.eventB)}
                                                                            <div class="change-value {changeDisplay.class}">
                                                                                {#if group.changeKeys.length > 1}
                                                                                    <span class="change-key-name">{changeKey}:</span>
                                                                                {/if}
                                                                                {#if changeDisplay.type === 'badges'}
                                                                                    <div class="badge-comparison">
                                                                                        <div class="badge-group">
                                                                                            <span class="badge-label">{m.compare_event_groups_before_label()}</span>
                                                                                            {#each changeDisplay.oldArray as item (item.id ?? item.name ?? item)}
                                                                                                <span class="badge badge-sm me-1 {item.colour === 'yellow' ? 'text-dark' : 'text-white'}" 
                                                                                                      style="background-color: {item.colour || '#6c757d'};">
                                                                                                    {item.name || item}
                                                                                                </span>
                                                                                            {:else}
                                                                                                <span class="text-muted small">{m.compare_event_groups_none_label()}</span>
                                                                                            {/each}
                                                                                        </div>
                                                                                        <div class="badge-group mt-1">
                                                                                            <span class="badge-label">{m.compare_event_groups_after_label()}</span>
                                                                                            {#each changeDisplay.newArray as item (item.id ?? item.name ?? item)}
                                                                                                <span class="badge badge-sm me-1 {item.colour === 'yellow' ? 'text-dark' : 'text-white'}" 
                                                                                                      style="background-color: {item.colour || '#6c757d'};">
                                                                                                    {item.name || item}
                                                                                                </span>
                                                                                            {:else}
                                                                                                <span class="text-muted small">{m.compare_event_groups_none_label()}</span>
                                                                                            {/each}
                                                                                        </div>
                                                                                    </div>
                                                                                {:else}
                                                                                    {changeDisplay.display}
                                                                                {/if}
                                                                            </div>
                                                                        {/each}
                                                                    </div>
                                                                {/each}
                                                            </div>
                                                            
                                                            <div class="action-buttons">
                                                                <button 
                                                                    type="button" 
                                                                    class="btn btn-sm btn-outline-primary me-1"
                                                                    onclick={(e) => {
                                                                        e.stopPropagation();
                                                                        applyChangeAtoB(aligned.indexA, aligned.indexB);
                                                                    }}
                                                                    title={m.compare_event_groups_apply_a_to_b_title()}>
                                                                    A → B
                                                                </button>
                                                                <button 
                                                                    type="button" 
                                                                    class="btn btn-sm btn-outline-primary"
                                                                    onclick={(e) => {
                                                                        e.stopPropagation();
                                                                        applyChangeBtoA(aligned.indexA, aligned.indexB);
                                                                    }}
                                                                    title={m.compare_event_groups_apply_b_to_a_title()}>
                                                                    B → A
                                                                </button>
                                                            </div>
                                                        </div>
                                                    {:else if difference.type === 'added'}
                                                        <div class="p-2">
                                                            <p class="small text-muted">{m.compare_event_groups_added_description()}</p>
                                                            <button 
                                                                type="button" 
                                                                class="btn btn-sm btn-outline-success"
                                                                onclick={(e) => {
                                                                    e.stopPropagation();
                                                                    applyChangeBtoA(aligned.indexA, aligned.indexB);
                                                                }}
                                                                title={m.compare_event_groups_copy_event_b_to_a_title()}>
                                                                {m.compare_event_groups_copy_to_a()}
                                                            </button>
                                                        </div>
                                                    {:else if difference.type === 'deleted'}
                                                        <div class="p-2">
                                                            <p class="small text-muted">{m.compare_event_groups_deleted_description()}</p>
                                                            <button 
                                                                type="button" 
                                                                class="btn btn-sm btn-outline-danger"
                                                                onclick={(e) => {
                                                                    e.stopPropagation();
                                                                    applyChangeAtoB(aligned.indexA, aligned.indexB);
                                                                }}
                                                                title={m.compare_event_groups_copy_event_a_to_b_title()}>
                                                                {m.compare_event_groups_copy_to_b()}
                                                            </button>
                                                        </div>
                                                    {/if}
                                                </div>
                                            {:else}
                                                <div class="no-difference d-flex flex-column align-items-center justify-content-center h-100">
                                                    <i class="bi bi-check-circle-fill text-success"></i>
                                                    <div class="small mt-1">{m.compare_event_groups_no_differences()}</div>
                                                </div>
                                            {/if}
                                        </div>
                                    </div>
                                </div>
                        
                                <!-- Group B Column -->
                                <div class="col-4 disabled-interaction">
                                    <div class="event-container">
                                        {#if eventB}
                                            <div class="event-wrapper">
                                                <div class="simple-event-display p-2">
                                                    <div class="event-number fw-bold text-muted mb-1">
                                                        B{aligned.indexB !== null ? aligned.indexB + 1 : '?'}
                                                        {#if aligned.similarity !== undefined}
                                                            <span class="similarity-badge badge badge-secondary ms-1" title={m.compare_event_groups_similarity_title({ percent: Math.round(aligned.similarity * 100) })}>
                                                                {Math.round(aligned.similarity * 100)}%
                                                            </span>
                                                        {/if}
                                                    </div>
                                                    <div class="event-card-properties">
                                                        {@render eventCardProperties(eventB)}
                                                    </div>
                                                </div>
                                            </div>
                                        {:else}
                                            <div class="event-wrapper empty-event">
                                                <div class="p-3 text-muted text-center">
                                                    <i class="bi bi-dash-circle"></i>
                                                    {m.compare_event_groups_no_event_added()}
                                                </div>
                                            </div>
                                        {/if}
                                    </div>
                                </div>
                            </div>
                        {/snippet}
                    </VList>
                    {/if}
                </div>
                
                <!-- Summary footer -->
                <div class="compare-footer p-3 border-top bg-light flex-shrink-0">
                    <div class="d-flex justify-content-center">
                        <div class="pagination-info">
                            {#if aligning}
                                {m.compare_event_groups_aligning()}
                            {:else}
                                {m.compare_event_groups_summary({ aligned: filteredAlignedEvents.length, differences: allDifferences.length })}
                            {/if}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>

<style>
.selected-row {
    background-color: rgba(13, 110, 253, 0.1) !important;
    box-shadow: inset 3px 0 0 #0d6efd;
    transition: all 0.2s ease;
}

.synchronized-row {
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.synchronized-row:hover {
    background-color: rgba(0, 0, 0, 0.05);
}

.modal-fullscreen .modal-content {
    height: 100vh;
    border-radius: 0;
    display: flex;
    flex-direction: column;
}

.modal-body {
    flex: 1;
    overflow: visible;
}

.compare-header {
    position: relative;
    z-index: 20;
}

.compare-content {
    height: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.aligning-status {
    flex: 1;
}

.compare-options {
    position: relative;
    display: inline-block;
}

.compare-options-popover {
    position: absolute;
    top: calc(100% + 0.25rem);
    left: 50%;
    transform: translateX(-50%);
    z-index: 1060;
    width: 320px;
    max-height: min(420px, 60vh);
    overflow: auto;
}

.column-headers {
    position: sticky;
    top: 0;
    z-index: 10;
    background: var(--bs-light);
    border-bottom: 1px solid var(--bs-border-color);
    flex-shrink: 0;
    overflow-y: auto;
    scrollbar-gutter: stable;
}

.synchronized-row {
    min-height: var(--compare-row-height, 280px);
    border-bottom: 1px solid var(--bs-border-color);
}

.event-container,
.difference-container {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.event-wrapper {
    height: var(--compare-row-height, 280px);
    min-height: var(--compare-row-height, 280px);
    max-height: var(--compare-row-height, 280px);
}

.empty-event {
    height: var(--compare-row-height, 280px);
    min-height: var(--compare-row-height, 280px);
    max-height: var(--compare-row-height, 280px);
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--bs-light);
}

.difference-item {
    height: var(--compare-row-height, 280px);
    min-height: var(--compare-row-height, 280px);
    max-height: var(--compare-row-height, 280px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.difference-content {
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
}

.no-difference {
    height: var(--compare-row-height, 280px);
    min-height: var(--compare-row-height, 280px);
    max-height: var(--compare-row-height, 280px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.changes-summary {
    max-height: 150px;
    overflow-y: auto;
    font-size: 0.85rem;
}

.change-item {
    margin-bottom: 0.5rem;
    padding: 0.25rem;
    background-color: var(--bs-light);
    border-radius: 0.25rem;
}

.change-item-compact {
    margin-bottom: 0.25rem;
    padding: 0.125rem 0.25rem;
    background-color: transparent;
    border-left: 3px solid var(--bs-primary);
    font-size: 0.75rem;
    line-height: 1.2;
}

.change-label {
    font-weight: 600;
    color: var(--bs-dark);
    margin-bottom: 0;
}

.change-item-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    margin-bottom: 0.125rem;
}

.change-property-actions {
    display: flex;
    gap: 0.25rem;
    flex-shrink: 0;
}

.change-property-actions .btn {
    font-size: 0.65rem;
    line-height: 1.1;
}

.change-key-name {
    font-weight: 600;
    margin-right: 0.25rem;
}

.change-value {
    font-family: 'Courier New', monospace;
    font-size: 0.7rem;
    word-break: break-all;
}

.timecode-change {
    color: var(--bs-primary);
    font-weight: 500;
}

.text-change {
    color: var(--bs-info);
    font-style: italic;
}

.object-change {
    color: var(--bs-warning);
    max-height: 40px;
    overflow: hidden;
}

.simple-change {
    color: var(--bs-secondary);
}

.voice-change {
    color: var(--bs-success);
    font-weight: 500;
}

.badge-change {
    font-family: inherit;
}

.badge-comparison {
    font-size: 0.65rem;
}

.badge-group {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.125rem;
}

.badge-label {
    font-weight: 600;
    color: var(--bs-dark);
    margin-right: 0.25rem;
    font-size: 0.6rem;
}

.badge-sm {
    font-size: 0.55rem;
    padding: 0.125rem 0.25rem;
}

.action-buttons {
    margin-top: auto;
    padding-top: 0.5rem;
}

.pagination-info {
    font-size: 0.9rem;
    color: var(--bs-secondary);
}

/* Simple event display styles */
.simple-event-display {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.simple-event-display .event-number {
    font-size: 0.875rem;
    flex-shrink: 0;
}

.event-card-properties {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    pointer-events: auto;
}

.event-prop {
    margin-bottom: 0.25rem;
}

.event-prop-timecode {
    font-family: 'Courier New', monospace;
}

.event-prop-label {
    font-weight: 600;
    margin-bottom: 0.125rem;
}

.event-prop-simple {
    display: flex;
    align-items: baseline;
    gap: 0.35rem;
    line-height: 1.2;
}

.event-prop-simple .text-muted {
    flex-shrink: 0;
}

.event-prop-badge-list {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.125rem;
}

.event-prop-html .text-content {
    font-size: 0.9rem;
    line-height: 1.4;
    max-height: 64px;
    overflow-y: auto;
    padding: 0.5rem;
    background-color: var(--bs-light);
    border-radius: 0.25rem;
    border: 1px solid var(--bs-border-color);
}

.note-line {
    font-size: 0.75rem;
    line-height: 1.2;
}

.note-heading {
    display: flex;
    align-items: center;
    gap: 0.35rem;
}

.note-label {
    font-weight: 600;
}

.note-text {
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    white-space: pre-wrap;
    color: var(--bs-body-color);
    margin-top: 0.125rem;
    padding-left: 1.15rem;
}

.simple-event-display .text-content:empty::before {
    content: 'No text content';
    color: var(--bs-secondary);
    font-style: italic;
}

/* Ensure columns maintain equal height */
.row.g-0 {
    min-height: 100%;
}

.col-4 {
    display: flex;
    flex-direction: column;
}

/* Disable interactions for columns 1 and 3 (Event Groups A and B) */
.disabled-interaction {
    pointer-events: none;
    user-select: none;
    position: relative;
}

.disabled-interaction::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(0.5px);
    z-index: 5;
    pointer-events: none;
}

.disabled-interaction .event-wrapper {
    opacity: 0.85;
    filter: grayscale(0.1);
}

/* Add a subtle visual indicator */
.disabled-interaction .event-wrapper::before {
    content: '🔒';
    position: absolute;
    top: 5px;
    right: 5px;
    font-size: 12px;
    opacity: 0.6;
    z-index: 10;
    pointer-events: none;
}

/* Ensure the compare column remains fully interactive */
.difference-container {
    pointer-events: auto;
    user-select: auto;
}

/* Override for empty events to maintain visual consistency */
.disabled-interaction .empty-event {
    opacity: 0.7;
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .compare-content {
        height: auto;
    }
    
    .column-headers .row {
        flex-direction: column;
    }
    
    .column-headers .col-4 {
        width: 100%;
        border-bottom: 1px solid var(--bs-border-color);
        border-right: none !important;
    }
    
    /* Maintain disabled state on mobile */
    .disabled-interaction {
        pointer-events: none;
    }
}
</style>